import { Injectable } from '@angular/core';
import { DetailRowId, DETAILS_ROW } from '@veritas-dumpsters/constants/gps-details-rows.const';
import { DumpsterSitGis, ICoordinationDetailsRow } from '@veritas-dumpsters/models/coordination-details-row';
import { DumpsterGpsInfo, ParametersCommand } from '@veritas-dumpsters/models/dumpster-gps-info';
import { DumpsterLocationService } from './dumpster-location.service';
import { Subject } from 'rxjs';
import { Dumpster } from '@veritas-shared/models/dumpster';
import { DumpsterService } from './dumpster.service';
import { filter, switchMap } from 'rxjs/operators';
import { dumpsterIcon, PATH_BASE_ASSET } from '@veritas-dumpsters/constants/dumpster-icon-path.const';
import { DateService } from '@veritas-shared/services/date.service';
import { DateFormat, LocalType } from '@veritas-shared/enums/date.enum';
import { GPS_COMMAND } from '@veritas-dumpsters/constants/dumpster-gps-info.const';

@Injectable({
  providedIn: 'root'
})
export class DumpsterGpsInfoService {
  private _dumpsterData$ = new Subject<string>();

  constructor(
    private locationService: DumpsterLocationService,
    private dumpsterService: DumpsterService,
    private dateService: DateService
  ){}

  public getCommandPayload = (): ParametersCommand<string> => {
    const currentDate: string = this.dateService.getFormat(new Date(), DateFormat.fullDate, { isUtc: true, locale: LocalType.eng });
    return ({
      enabled: true,
      ruleName: GPS_COMMAND,
      parameters: [{
        parameterName: GPS_COMMAND,
        value: `${currentDate}+00:00`
      }]
    })
  }

  public getPinIcon = (isSitGis: boolean): string => {
    if (isSitGis) return `${PATH_BASE_ASSET}/${dumpsterIcon.blue}`
    return `${PATH_BASE_ASSET}/${dumpsterIcon.green}`;
  }

  public getDumpsterRow = (details?: DumpsterGpsInfo): ICoordinationDetailsRow[] => {
    if (!details) return DETAILS_ROW;
    return DETAILS_ROW.map((row: ICoordinationDetailsRow): ICoordinationDetailsRow => this._getRow(row, details));
  }

  public setDumpsterId = (dumpsterId: string) => { this._dumpsterData$.next(dumpsterId); }

  public getMapPins = (dumpster: Dumpster, details?: DumpsterGpsInfo): DumpsterSitGis[] => {
    if (!dumpster)return [];
    const dumpsterSitGis: DumpsterSitGis[] = this._getDumpsterSitGis(dumpster, details);
    const dumpsterCurrent: DumpsterSitGis = {
      ...dumpster,
      isSitGis: false,
    }

    return [dumpsterCurrent, ...dumpsterSitGis];
  }

  private _getDumpsterSitGis = (dumpster: Dumpster, details?: DumpsterGpsInfo): DumpsterSitGis[] => {
    if (!details?.latitudeSIT && !details?.longitudeSIT) return [];
    return [{
      ...dumpster,
      isSitGis: true,
      location: {
        latitude: details?.latitudeSIT,
        longitude: details?.longitudeSIT
      }
    }]
  }

  public dumpsterData$ = this._dumpsterData$.asObservable().pipe(
    filter((dumpsterId: string) => Boolean(dumpsterId)),
    switchMap((dumpsterId: string) => this.dumpsterService.getGpsInfo(dumpsterId)),
  );

  private _getRow = (row: ICoordinationDetailsRow, details: DumpsterGpsInfo): ICoordinationDetailsRow => {
    if (row.id === DetailRowId.coordination) {
      return {
        ...row,
        value: this.locationService.getLocationString(details?.latitudeGPS, details?.longitudeGPS),
        valueSitGis: this.locationService.getLocationString(details?.latitudeSIT, details?.longitudeSIT),
      }
    }
    if (row.id === DetailRowId.lastEdit){
      return {
        ...row,
        value: this.dateService.getFormat(details?.lastGpsSync, DateFormat.defaultDate),
        valueSitGis: this.dateService.getFormat(details?.lastSitChangeTimestamp, DateFormat.defaultDate),
      }
    }
    return {
      ...row,
      value: details?.addressGPS,
      valueSitGis: details?.addressSIT,
    }
  }
}
